// Short share link rules shared by api/ssr.ts (full page loads) and the client
// (src/utils/acquisitionAttribution.ts, src/utils/publicShareUrls.ts). No
// imports, so both the api/ ESM runtime and the Vite app can load it.

// /r/jp/<page> opens /journal-prompts/<page>.
export const PROMPT_SHORT_SHARE_KIND = 'jp';
export const PROMPT_PAGES_PATH = '/journal-prompts';
// A prompt page segment must be a plain lowercase slug: letters, digits and
// single hyphens. It is checked before any decoding, so encoded slashes, dots
// and other escapes never reach a redirect.
export const PROMPT_PAGE_SLUG_PATTERN = /^[a-z0-9]+(?:-[a-z0-9]+)*$/;

// Enough rounds to unwrap any realistic multi-encoded value. A value that is
// still changing after this many rounds is treated as unsafe.
const MAX_DECODE_ROUNDS = 5;

// Decodes only ASCII escapes (%00 to %7F). It never throws on malformed or
// non-ASCII escapes, unlike decodeURIComponent, and ASCII is all the dot
// segment check needs: ".", "/", "\" and "%" itself.
function decodeAsciiEscapes(value: string): string {
  return value.replace(/%([0-7][0-9a-f])/gi, (_, hex: string) => String.fromCharCode(parseInt(hex, 16)));
}

/** True for /journal-prompts and /journal-prompts/<plain lowercase slug>. */
export function isPromptPagePath(path: string): boolean {
  if (path === PROMPT_PAGES_PATH) return true;
  const prefix = `${PROMPT_PAGES_PATH}/`;
  return path.startsWith(prefix) && PROMPT_PAGE_SLUG_PATTERN.test(path.slice(prefix.length));
}

/**
 * Builds /journal-prompts/<page> from the raw /r/jp/ page segment, or null when
 * the segment is not a plain slug. The segment is lowercased first, so
 * /r/jp/Gratitude opens /journal-prompts/gratitude.
 */
export function buildPromptPageTargetPath(segment: string | null | undefined): string | null {
  if (!segment) return null;
  const slug = segment.toLowerCase();
  return PROMPT_PAGE_SLUG_PATTERN.test(slug) ? `${PROMPT_PAGES_PATH}/${slug}` : null;
}

/**
 * True when the path part of value (before ? and #) has a "." or ".." segment,
 * split on "/" or "\", in its raw form or after any round of decoding. URL
 * parsers resolve those segments (including %2e%2e), so a next path such as
 * /p/../admin would otherwise leave the allowlisted prefix.
 */
export function hasDotPathSegment(value: string): boolean {
  let current = value.split(/[?#]/)[0] ?? '';

  for (let round = 0; round <= MAX_DECODE_ROUNDS; round += 1) {
    if (current.split(/[\\/]/).some((segment) => segment === '.' || segment === '..')) {
      return true;
    }

    const decoded = decodeAsciiEscapes(current);
    if (decoded === current) return false;
    current = decoded;
  }

  return true;
}

// /r/home, /r/live and /r/app are fixed tracked links with no page segment.
// They take the same s/m/c/t/ct params as the other short links.
export const FIXED_SHORT_LINK_MEDIUM = 'short_link';
const FIXED_SHORT_LINK_PATHS: Record<string, string> = { home: '/', live: '/live' };
export const APP_SHORT_LINK_KIND = 'app';
// App Store URLs drop UTMs, so /r/app passes the source as the ct campaign
// token, which App Store Connect reports by campaign. No provider token (pt)
// is configured, so none is added.
export const APP_STORE_SHORT_LINK_URL = 'https://apps.apple.com/app/id1661995476';

/** The fixed short link kind (home, live or app) for a /r/ or /ref/ path, else null. */
export function getFixedShortLinkKind(kind: string | undefined, extraSegmentCount: number): string | null {
  if (!kind || extraSegmentCount > 0) return null;
  const normalized = kind.toLowerCase();
  return normalized === APP_SHORT_LINK_KIND || normalized in FIXED_SHORT_LINK_PATHS ? normalized : null;
}

/** /r/home opens /, /r/live opens /live. Null for app and anything else. */
export function getFixedShortLinkPath(fixedKind: string | null): string | null {
  return fixedKind && fixedKind in FIXED_SHORT_LINK_PATHS ? FIXED_SHORT_LINK_PATHS[fixedKind] : null;
}

/** The App Store listing, with the source as the ct campaign token when there is one. */
export function buildAppStoreShortLinkUrl(source: string | null): string {
  const url = new URL(APP_STORE_SHORT_LINK_URL);
  if (source) url.searchParams.set('ct', source);
  return url.toString();
}
